import { Button, Radio } from 'components/core';
import { getIconByNotificationType } from 'components/core/Notification';
import { useI18n } from 'contexts/i18n';
import getConfig from 'next/config';
import Image from 'next/legacy/image';
import { FC, useEffect } from 'react';
import { AiOutlinePlusCircle } from 'react-icons/ai';
import { useIteroPayments } from 'reactQuery/queries';
import { LoadingSpinner, LoadingWrapper } from 'styles/sharedStyles';
import { CreditCardClientData } from 'types';
import { EuOrderPaymentProps, Styled } from '.';
import {
  getCreditCardLogo,
  reorderCreditCards,
  shouldDisableSubmitPaymentEU,
  shouldDisplayAddCardOption,
} from './ITeroOrderPayment.helper';
import { useReadOnlyUser } from 'utils/hooks';

const EuOrderPayment: FC<EuOrderPaymentProps> = ({
  mode,
  onSubmit,
  selectedCreditCard,
  setCreditCard,
  paymentType,
  tmSelectedScannerFundingSource,
}) => {
  const { publicRuntimeConfig } = getConfig();
  const i18n = useI18n();
  const {
    data: paymentMethods,
    isLoading: isCreditCardLoading,
    isIdle,
    isError,
  } = useIteroPayments(paymentType, tmSelectedScannerFundingSource);
  const { isReadOnlyUser } = useReadOnlyUser();

  useEffect(() => {
    if (
      paymentType === 'subscription' &&
      paymentMethods &&
      paymentMethods.cards.length > 0 &&
      typeof selectedCreditCard === 'undefined'
    ) {
      const defaultCreditCard = paymentMethods.cards.filter(
        card => card.default
      )[0];
      if (defaultCreditCard) {
        setCreditCard(defaultCreditCard);
      } else {
        setCreditCard(paymentMethods.cards[0]);
      }
    }
  }, [paymentMethods]);

  if (isCreditCardLoading || isIdle) {
    return (
      <LoadingWrapper>
        <LoadingSpinner width="25px" height="25px" isPrimary={true} />
      </LoadingWrapper>
    );
  }

  const handleCreditCardChange = (creditCard: CreditCardClientData) => {
    setCreditCard(creditCard);
  };

  const handleOtherPaymentMethods = () => {
    setCreditCard(undefined);
  };

  const shouldShowCashCheck =
    paymentType !== 'subscription' &&
    tmSelectedScannerFundingSource === 'Cash/Check';

  if (isError) {
    return (
      <>{i18n['Something went wrong while fetching credit card detail.']}</>
    );
  }

  return (
    <>
      {mode === 'create' && (
        <>
          {paymentType === 'scanner' && (
            <Styled.Notification>
              <Styled.NotificationIcon>
                {getIconByNotificationType('info')}
              </Styled.NotificationIcon>
              <Styled.NotificationBody>
                {
                  i18n[
                    'Your preferred method shall be used towards your scanner payment, processing fee, and ancillary products payment (if any)'
                  ]
                }
              </Styled.NotificationBody>
            </Styled.Notification>
          )}
          {paymentType === 'subscription' && (
            <Styled.Notification>
              <Styled.NotificationIcon>
                {getIconByNotificationType('info')}
              </Styled.NotificationIcon>
              <Styled.NotificationBody>
                {
                  i18n[
                    'Your payment method shall be applicable towards your future service term plan'
                  ]
                }
              </Styled.NotificationBody>
            </Styled.Notification>
          )}
          {paymentType === 'rental' && (
            <Styled.Notification>
              <Styled.NotificationIcon>
                {getIconByNotificationType('info')}
              </Styled.NotificationIcon>
              <Styled.NotificationBody>
                {
                  i18n[
                    'Your payment method shall be applicable towards your rental payments.'
                  ]
                }
              </Styled.NotificationBody>
            </Styled.Notification>
          )}

          {shouldShowCashCheck && (
            <Styled.PayByInvoiceContainer
              isSelected={selectedCreditCard === undefined}
              onClick={handleOtherPaymentMethods}
            >
              <Radio
                checked={selectedCreditCard === undefined}
                onChange={handleOtherPaymentMethods}
              />
              <Image
                src={getCreditCardLogo('invoice')}
                alt="logo"
                width={40}
                height={20}
              />
              <Styled.PayByInvoiceTitle>
                {'Cash/Check'}
              </Styled.PayByInvoiceTitle>
            </Styled.PayByInvoiceContainer>
          )}

          {tmSelectedScannerFundingSource &&
            reorderCreditCards(paymentMethods?.cards)?.map(creditCard => {
              return (
                <Styled.CreditCardContainer
                  key={creditCard.id}
                  isSelected={selectedCreditCard?.id === creditCard.id}
                  onClick={() => handleCreditCardChange(creditCard)}
                >
                  <Styled.CreditCard>
                    <Radio
                      checked={creditCard.id === selectedCreditCard?.id}
                      onChange={() => handleCreditCardChange(creditCard)}
                    />
                    <Image
                      src={getCreditCardLogo(creditCard.cardType)}
                      alt={`${creditCard.cardType} logo`}
                      width={40}
                      height={40}
                    />
                    <Styled.CardHolderName>
                      {creditCard.name}
                    </Styled.CardHolderName>
                    <Styled.LastDigits>
                      {`${i18n['ending in']} ${creditCard.last4Digit}`}{' '}
                    </Styled.LastDigits>
                    <Styled.Expiry>{`${i18n['Exp:']} ${creditCard.expiry}`}</Styled.Expiry>
                  </Styled.CreditCard>
                </Styled.CreditCardContainer>
              );
            })}

          {tmSelectedScannerFundingSource &&
            paymentMethods?.others.map(paymentOption => {
              return (
                <Styled.PayByInvoiceContainer
                  key={paymentOption.label}
                  isSelected={
                    paymentOption.value === tmSelectedScannerFundingSource
                  }
                  onClick={() => handleOtherPaymentMethods()}
                >
                  <Radio
                    checked={
                      paymentOption.value === tmSelectedScannerFundingSource
                    }
                    onChange={() => handleOtherPaymentMethods()}
                  />

                  {paymentOption.logo && (
                    <Image
                      src={getCreditCardLogo(paymentOption.logo)}
                      alt="logo"
                      width={24}
                      height={24}
                    />
                  )}
                  <Styled.PayByInvoiceTitle>
                    {paymentOption.label}
                  </Styled.PayByInvoiceTitle>
                </Styled.PayByInvoiceContainer>
              );
            })}

          {tmSelectedScannerFundingSource === undefined && (
            <Styled.PayByInvoiceTitle error>
              {i18n['Itero Invalid Funding Source']}
            </Styled.PayByInvoiceTitle>
          )}

          {tmSelectedScannerFundingSource && (
            <Styled.ButtonWrapper>
              {shouldDisplayAddCardOption(
                paymentType,
                tmSelectedScannerFundingSource
              ) && (
                <Button
                  onClick={() => {
                    window.open(publicRuntimeConfig.PAYMENT_APP_URL, '_blank');
                  }}
                  variant="secondary"
                  disabled={isReadOnlyUser}
                >
                  <AiOutlinePlusCircle />

                  <Styled.ButtonLabel>
                    {i18n['Add Payment Method']}
                  </Styled.ButtonLabel>
                </Button>
              )}
              <Styled.ContinuetoReviewButton>
                <Button
                  size="md"
                  onClick={onSubmit}
                  disabled={shouldDisableSubmitPaymentEU(
                    paymentType,
                    paymentMethods.cards,
                    selectedCreditCard,
                    tmSelectedScannerFundingSource
                  )}
                >
                  {i18n['Next']}
                </Button>
              </Styled.ContinuetoReviewButton>
            </Styled.ButtonWrapper>
          )}
        </>
      )}
      {mode === 'edit' && (
        <>
          {selectedCreditCard?.id && (
            <>
              <Styled.CreditCardReadOnly>
                <Image
                  src={getCreditCardLogo(selectedCreditCard.cardType)}
                  alt="logo"
                  width={40}
                  height={40}
                />
                <Styled.CardHolderName>
                  {selectedCreditCard.name}
                </Styled.CardHolderName>
                <Styled.LastDigits>
                  {`${i18n['ending in']} ${selectedCreditCard.last4Digit}`}{' '}
                </Styled.LastDigits>
                <Styled.Expiry>{`${i18n['Exp:']} ${selectedCreditCard.expiry}`}</Styled.Expiry>
              </Styled.CreditCardReadOnly>
            </>
          )}
          {!selectedCreditCard &&
            tmSelectedScannerFundingSource === 'Financing' && (
              <Styled.CreditCardReadOnly>
                <Styled.PayByInvoiceTitle>
                  <span>{tmSelectedScannerFundingSource}</span>
                </Styled.PayByInvoiceTitle>
              </Styled.CreditCardReadOnly>
            )}
          {!selectedCreditCard &&
            tmSelectedScannerFundingSource === 'Cash/Check' && (
              <Styled.CreditCardReadOnly>
                <Styled.PayByInvoiceTitle>
                  <span>Cash/Check</span>
                </Styled.PayByInvoiceTitle>
              </Styled.CreditCardReadOnly>
            )}
        </>
      )}
    </>
  );
};

export default EuOrderPayment;
